import React, { useState, useEffect, useRef } from 'react'
import { Card, Row, Col, Tabs, Tab, Button } from 'react-bootstrap'
import FeatherIcon from 'feather-icons-react'
import { useLocation } from 'react-router-dom'
import DateRangePickerCustom, {
  DateDefine,
} from 'components/DatePicker/DateRangePickerCustom'
import ModalWrapper from 'designer/external/ModalWrapper'
import moment from 'moment'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import HighchartsExporting from 'highcharts/modules/exporting'
import HighchartsGrouped from 'highcharts-grouped-categories'
import HighchartsMore from 'highcharts/highcharts-more'
import HighchartsHeatmap from 'highcharts/modules/heatmap'
import classNames from 'classnames/bind'
import './highcharts.css'
import httpClient from 'utility/httpClient.util'
import { getFluidTotal } from './fluidTotal.util'
import { startOfDay } from 'date-fns'
import useAuth from 'hooks/useAuth'
import { useTimeFormat } from 'contexts/TimeFormatContext'
import _ from 'lodash'
import {
  TEMPLATE_OPTIONS,
  BP_OPTIONS,
  TEMP_OPTIONS,
  PAIN_OPTIONS,
  OTHER_OPTIONS,
  BP_PR_RANGE_OPTIONS,
  BP_HM_OPTIONS,
} from './highcharts-options'
import DesignerModal from 'designer/external/DesignerModal'
import { Skeleton } from '@mui/material'
import ChartSkeleton from './ChartSkeleton'
HighchartsHeatmap(Highcharts)
HighchartsExporting(Highcharts)
HighchartsGrouped(Highcharts)
HighchartsMore(Highcharts)

// Function to synchronize extremes across charts
function syncExtremes(e) {
  const thisChart = this.chart
  if (e.trigger !== 'syncExtremes') {
    // Prevent feedback loop
    Highcharts.each(Highcharts.charts, function (chart) {
      console.log('trigger syncEx')
      if (chart && chart !== thisChart) {
        if (chart.xAxis[0].setExtremes) {
          // It is null while updating
          chart.xAxis[0].setExtremes(e.min, e.max, undefined, false, {
            trigger: 'syncExtremes',
          })
        }
      }
    })
  }
}

const MODAL_VITAL_SIGN = 'modal-add-vital-sign'
const O2_SATURATION_FIELDS = [
  { key: 'o2Saturation', label: 'O2 Sat' },
  { key: 'o2SaturationRa', label: 'O2 Sat RA' },
  { key: 'o2SaturationOn', label: 'O2 Sat On' },
]
const O2_SATURATION_KEYS = O2_SATURATION_FIELDS.map(({ key }) => key)

export default function MonitorChart(props) {
  const isPrintMode = props?.showInput === false
  const chartHeights = isPrintMode
    ? {
        header: '58px',
        mercury: '255px',
        bp: '76px',
        wh: '58px',
        su: '42px',
        iov: '42px',
        iot: '58px',
        score: '105px',
      }
    : {
        header: '63px',
        mercury: '320px',
        bp: '80px',
        wh: '65px',
        su: '40px',
        iov: '40px',
        iot: '64px',
        score: '125px',
      }
  const { formatDate } = useTimeFormat()
  const chartComponentRef = useRef(null)
  const chartBpComponentRef = useRef(null)
  const customLabelRef = useRef(null)
  const customBpLabelRef = useRef(null)
  const chartOtherComponentRef = useRef(null)
  const customOtherLabelRef = useRef(null)
  const { config } = useAuth()
  const location = useLocation()
  //const queryParams = new URLSearchParams(location.search)
  const hash = window.location.hash
  // Extract the query string part from the hash. This depends on the structure of your URLs.
  const queryString = hash.substring(hash.indexOf('?'))
  const queryParams = new URLSearchParams(queryString)
  const hn = queryParams.get('hn')
  const vn = queryParams.get('vn')
  const an = props?.an || queryParams.get('an')

  const [printList, setPrintList] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [mercury, setMercury] = useState({
    ..._.cloneDeep(TEMPLATE_OPTIONS),
    ...TEMP_OPTIONS,
  })
  const [monHeader, setMonHeader] = useState({
    ..._.cloneDeep(TEMPLATE_OPTIONS),
    ..._.cloneDeep(BP_HM_OPTIONS),
  })
  const [monMercury, setMonMercury] = useState({
    ..._.cloneDeep(TEMPLATE_OPTIONS),
    ..._.cloneDeep(TEMP_OPTIONS),
  })
  const [monBpHeatmap, setMonBpHeatmap] = useState({
    ..._.cloneDeep(TEMPLATE_OPTIONS),
    ..._.cloneDeep(BP_HM_OPTIONS),
  })
  const [monWhHeatmap, setMonWhHeatmap] = useState({
    ..._.cloneDeep(TEMPLATE_OPTIONS),
    ..._.cloneDeep(BP_HM_OPTIONS),
  })
  const [monSuHeatmap, setMonSuHeatmap] = useState({
    ..._.cloneDeep(TEMPLATE_OPTIONS),
    ..._.cloneDeep(BP_HM_OPTIONS),
  })
  const [monIOVHeatmap, setMonIOVHeatmap] = useState({
    ..._.cloneDeep(TEMPLATE_OPTIONS),
    ..._.cloneDeep(BP_HM_OPTIONS),
  })
  const [monIOTHeatmap, setMonIOTHeatmap] = useState({
    ..._.cloneDeep(TEMPLATE_OPTIONS),
    ..._.cloneDeep(BP_HM_OPTIONS),
  })
  const [monScoreHeatmap, setMonScoreHeatmap] = useState({
    ..._.cloneDeep(TEMPLATE_OPTIONS),
    ..._.cloneDeep(BP_HM_OPTIONS),
  })
  const [bpOptions, setBPOptions] = useState({
    ..._.cloneDeep(TEMPLATE_OPTIONS),
    ...BP_OPTIONS,
  })
  const [painOptions, setPainOptions] = useState({
    ..._.cloneDeep(TEMPLATE_OPTIONS),
    ...PAIN_OPTIONS,
  })
  const [otherOptions, setOtherOptions] = useState({
    ..._.cloneDeep(TEMPLATE_OPTIONS),
    ...OTHER_OPTIONS,
  })
  const [bpPrOptions, setBpPrOptions] = useState({
    ..._.cloneDeep(TEMPLATE_OPTIONS),
    ...BP_PR_RANGE_OPTIONS,
  })
  const [monDietHeatmap, setMonDietHeatmap] = useState({
    ..._.cloneDeep(TEMPLATE_OPTIONS),
    ..._.cloneDeep(BP_HM_OPTIONS),
  })

  const [admitInfo, setAdmitInfo] = useState(null)
  const [firstDateVital, setFirstDateVital] = useState(null)
  let tabsList = [
    { title: 'ฟอร์มปรอท', name: 'monitoring' },
    { title: 'V/S Chart', name: 'chart' },
    { title: 'Pain Scale', name: 'painScale' },
    { title: 'Blood Pressure', name: 'bp' },
    // { title: 'Other', name: 'other' },
  ]

  const [tabKey, setTabKey] = useState(tabsList[0]?.name)
  const [valueDateRange, setValueDateRange] = useState([null, null])

  const [modalCode, setModalCode] = useState(false)
  const [isPrinting, setIsPrinting] = useState(false)
  const [timeScale, setTimeScale] = useState('hour')

  const findNearestSlotTime = (timeCategories, time) => {
    const [hourStr, minStr] = time.split(':')
    const actualHour = parseInt(hourStr, 10)
    const actualMinute = parseInt(minStr, 10)

    // Convert time to minutes
    const actualTimeInMinutes = actualHour * 60 + actualMinute

    // Find nearest time slot
    let nearestSlot = timeCategories[0]
    let minDiff = Math.abs(actualTimeInMinutes - nearestSlot * 60)

    for (const slot of timeCategories) {
      const slotInMinutes = slot * 60
      const diff = Math.abs(actualTimeInMinutes - slotInMinutes)
      if (diff < minDiff) {
        minDiff = diff
        nearestSlot = slot
      }
    }

    //console.log(`Actual time: ${time} => Nearest slot: ${String(nearestSlot).padStart(2, '0')}:00`);
    return String(nearestSlot).padStart(2, '0')
  }

  const applyPrintYAxisLabels = (options) => {
    if (!isPrintMode) return

    options.yAxis.labels = {
      ...(options.yAxis.labels || {}),
      allowOverlap: true,
      step: 1,
      style: {
        ...(options.yAxis.labels?.style || {}),
        fontSize: '10px',
        whiteSpace: 'nowrap',
        textOverflow: 'none',
      },
    }
  }

  const mapToThreeSlot = (hour) => {
    const h = parseInt(hour, 10)
    if (Number.isNaN(h)) return null

    if (h >= 0 && h < 8) return '02'
    if (h >= 8 && h < 16) return '10'
    return '18'
  }

  const openModal = (modalCode) => setModalCode(modalCode)
  const onCloseModal = () => setModalCode(false)

  Highcharts.Pointer.prototype.reset = () => {}

  Highcharts.Point.prototype.highlight = function (event) {
    this.onMouseOver()
    //this.series.chart.tooltip.refresh(this);
    //this.series.chart.xAxis[0].drawCrosshair(event, this);
  }

  const initChart = () => {
    setMonMercury({
      ..._.cloneDeep(TEMPLATE_OPTIONS),
      ..._.cloneDeep(TEMP_OPTIONS),
    })
  }

  const handleMouseMove2 = (e) => {}
  const handleMouseMove = (e) => {
    let point = null
    let event = null

    e.persist()
    Highcharts.charts.forEach((chart, chartIndex) => {
      if (!chart) return

      event = chart.pointer.normalize(e)
      chart.series.forEach((item, index) => {
        point = chart.series[index].searchPoint(event, true)
        if (point) {
          point.highlight(e)
        }
      })
    })
  }

  const addHeaderTable = (chart, labelRef) => {
    //const chart = chartComponentRef.current.chart;
    labelRef.current = chart.renderer
      .label('Date ', 50, 20)
      .attr({
        zIndex: 5,
      })
      .css({
        color: 'rgb(102, 102, 102)',
        fontSize: '12px',
      })
      .add()

    labelRef.current = chart.renderer
      .label('Time', 50, 40)
      .attr({
        zIndex: 5,
      })
      .css({
        color: 'rgb(102, 102, 102)',
        fontSize: '12px',
      })
      .add()

    const labelBBox = labelRef.current.getBBox()
    const lineYPosition = labelBBox.y + labelBBox.height + 22 // 5 pixels below the label

    // Add a horizontal line below the label
    labelRef.current = chart.renderer
      .path([
        'M',
        30,
        lineYPosition,
        'L',
        30 + labelBBox.width + 30,
        lineYPosition,
      ])
      .attr({
        'stroke-width': 1.5,
        stroke: 'rgb(204 214 234)',
        zIndex: 5,
      })
      .add()
  }

  useEffect(() => {
    if (chartComponentRef.current) {
      !customLabelRef.current &&
        addHeaderTable(chartComponentRef.current.chart, customLabelRef)
      !customBpLabelRef.current &&
        addHeaderTable(chartBpComponentRef.current.chart, customBpLabelRef)
      // !customOtherLabelRef.current &&
      //   addHeaderTable(
      //     chartOtherComponentRef.current.chart,
      //     customOtherLabelRef
      //   )
    }
  }, [isLoading])

  const loadVitalSign = async (start, end) => {
    setIsLoading(true)
    if (!an) {
      initChart()
      setIsLoading(false)
      return
    }
    //const response = await httpClient.get(`/ipd/in-patient-admit/history/${hn}`)
    const response = await httpClient.get(
      `/ipd/in-patient-admit/profile?an=${an}&include=patient.person,patient.Attachment,PatientAllergy,Ward`,
    )
    const admitInfo = response.data.data
    setAdmitInfo(admitInfo)
    //setVnList(list)
    let startOfDay = DateDefine.startOfToday
    // let find = list.find(({ an, vn, hn }) => an === an)
    // if (find) {
    //   startOfDay = new Date(find.checkInDate)
    // }
    startOfDay = new Date(admitInfo.checkInDate)
    // Check if startOfDay is more than 7 days ago
    const endOfToday =
      admitInfo && admitInfo.checkOutDate
        ? new Date(admitInfo.checkOutDate)
        : DateDefine.endOfToday
    setValueDateRange([startOfDay, endOfToday])

    if (props?.startDate && props?.endDate) {
      await refreshData(props?.startDate, props?.endDate, admitInfo.checkInDate)
    } else {
      if (start && end) {
        await refreshData(start, end, admitInfo ? admitInfo.checkInDate : true)
      } else {
        const startDate = moment(startOfDay).format('YYYY-MM-DD')
        const endDate = moment(endOfToday).format('YYYY-MM-DD')
        await refreshData(
          startDate,
          endDate,
          admitInfo ? admitInfo.checkInDate : true,
        )
      }
    }
    setIsLoading(false)
  }

  useEffect(loadVitalSign, [location, an])

  const refreshData = async (startDate, endDate, checkInDate) => {
    const response2 = await httpClient.get(
      `/ipd/in-patient-vital-sign/an/${an}?startDate=${startDate}&endDate=${endDate}`,
    )

    const timeCategories = ['02', '06', '10', '14', '18', '22']
    const threeSlotCategories = ['02', '10', '18']

    const sortDataByDateTime = (data) => {
      return data.sort((a, b) => {
        const dateTimeA = new Date(`${a.jsonData.date}T${a.jsonData.time}`)
        const dateTimeB = new Date(`${b.jsonData.date}T${b.jsonData.time}`)
        return dateTimeA - dateTimeB
      })
    }
    const result = {}
    const sortedData = sortDataByDateTime(response2.data.data)

    let firstData = sortedData[0]
    if (firstData) {
      let date = firstData.date
      setFirstDateVital(new Date(date))
      if (!checkInDate) {
        setValueDateRange([new Date(date), new Date(endDate)])
      }
    }

    //console.log("sort",sortedData)
    //const parseValue = (value, parseFunc) => (value && value !== '' ? parseFunc(value) : null);
    const parseValue = (value, parseFunc) => {
      if (value && value !== '') {
        const parsed = parseFunc(value)
        if (isNaN(parsed)) {
          return value // Return null if it's not a valid integer
        }
        return parsed
      }
      return value || ''
    }
    const parseNumberValue = (value) => {
      if (value === null || value === undefined || value === '') return null
      const parsed = Number(value)
      return Number.isNaN(parsed) ? null : parsed
    }
    const hasVitalSignValue = (value) =>
      value !== null && value !== undefined && value !== ''
    const o2SaturationFieldsWithData = O2_SATURATION_FIELDS.filter(({ key }) =>
      sortedData.some((item) => hasVitalSignValue(item?.jsonData?.[key])),
    )
    const visibleO2SaturationFields = o2SaturationFieldsWithData.length
      ? o2SaturationFieldsWithData
      : [O2_SATURATION_FIELDS[0]]
    const visibleO2SaturationKeys = visibleO2SaturationFields.map(
      ({ key }) => key,
    )
    const getBpChartHeight = (rowCount) =>
      `${rowCount * (isPrintMode ? 19 : 20)}px`
    const mergeSlotVitalSignData = (
      slotData,
      parsedData,
      commentData,
      inPatientVitalSignId,
      keys,
    ) => {
      const nextData = { ...slotData }
      const nextCommentData = { ...(slotData?.commentData || {}) }
      const nextVitalSignIds = { ...(slotData?.vitalSignIds || {}) }

      keys.forEach((key) => {
        if (!hasVitalSignValue(parsedData[key])) return

        nextData[key] = parsedData[key]
        nextVitalSignIds[key] = inPatientVitalSignId
        nextCommentData[key] = commentData?.[key] || []
      })

      return {
        ...nextData,
        commentData: nextCommentData,
        vitalSignIds: nextVitalSignIds,
        inPatientVitalSignId,
      }
    }

    const keys = [
      'hr',
      'rr',
      'temp',
      'bpMin',
      'bpMax',
      'painScale',
      'others',
      'weight',
      'height',
      'bmi',
      ...O2_SATURATION_KEYS,
      'stools',
      'urine',
      'intakeOralFluids',
      'intakeParenteral',
      'intakeTotal',
      'outputTotal',
      'fallScore',
      'ppsScore',
      'sedationScore',
      'adl',
      'dtx',
    ]
    let dateList = {}

    // sortedData.forEach((item) => {
    //   const { date, time, jsonData } = item
    //   const hour = time.split(':')[0] // Extracting the hour part

    //   // Check if the time exists in timeCategories
    //   if (timeCategories.includes(hour)) {
    //     if (!result[date]) {
    //       // Initialize the result for the date if it doesn't exist
    //       result[date] = timeCategories.reduce((acc, time) => {
    //         acc[time] = keys.reduce((timeAcc, key) => {
    //           timeAcc[key] = null
    //           return timeAcc
    //         }, {})
    //         return acc
    //       }, {})
    //     }

    //     // Prepare parsed data object for the specific item
    //     const parsedData = keys.reduce((acc, key) => {
    //       acc[key] = parseValue(
    //         jsonData[key],
    //         ['temp', 'weight', 'height'].includes(key)
    //           ? parseFloat
    //           : (val) => parseInt(val, 10),
    //       )
    //       return acc
    //     }, {})

    //     // Update the result with parsed values
    //     result[date][hour] = {
    //       ...result[date][hour],
    //       ...parsedData,
    //     }
    //   }

    //   if (!dateList[date]) dateList[date] = []
    //   dateList[date].push(moment(item.time, 'HH:mm:ss').format('HH:mm'))
    // })
    sortedData.forEach((item) => {
      const { date, time, jsonData, commentData, inPatientVitalSignId } = item

      if (!result[date]) {
        // Initialize the result for the date if it doesn't exist
        result[date] = timeCategories.reduce((acc, time) => {
          acc[time] = keys.reduce((timeAcc, key) => {
            timeAcc[key] = null
            return timeAcc
          }, {})
          return acc
        }, {})
      }

      let hour = time.split(':')[0] // Extracting the hour part

      if (!timeCategories.includes(hour)) {
        //scale
        if (timeScale === 'hour') {
          hour = findNearestSlotTime(timeCategories, time)
        }
      }
      if (timeScale === 'actual') {
        hour = moment(item.time, 'HH:mm:ss').format('HH:mm')
      }
      // Check if the time exists in timeCategories
      if (timeCategories.includes(hour) || timeScale === 'actual') {
        // Prepare parsed data object for the specific item
        const parsedData = keys.reduce((acc, key) => {
          acc[key] = parseValue(
            jsonData[key],
            ['temp', 'weight', 'height', 'bmi'].includes(key)
              ? parseFloat
              : (val) => parseInt(val, 10),
          )
          return acc
        }, {})
        parsedData.intakeTotal = getFluidTotal(jsonData, 'intakeTotal', [
          'intakeOralFluids',
          'intakeParenteral',
        ])
        parsedData.outputTotal = getFluidTotal(jsonData, 'outputTotal', [
          'outputUrine',
          'outputOther',
        ])

        // Update only fields present in this record so later partial records do not clear earlier values in the same slot.
        result[date][hour] = mergeSlotVitalSignData(
          result[date][hour],
          parsedData,
          commentData,
          inPatientVitalSignId,
          keys,
        )
      }

      if (!dateList[date]) dateList[date] = []
      dateList[date].push(moment(item.time, 'HH:mm:ss').format('HH:mm'))
    })
    const vitalData = {
      hr: [],
      rr: [],
      temp: [],
      bpMin: [],
      bpMax: [],
      painScale: [],
      others: [],
    }
    const start = moment(startDate)
    const end = moment(endDate)
    const categories = []
    // Create an array of all dates between startDate and endDate
    const allDates = []
    while (start.isSameOrBefore(end)) {
      allDates.push(start.format('YYYY-MM-DD')) // Format the date as needed
      start.add(1, 'days') // Move to the next day
    }

    let allTimes = []
    let threeSlotTimes = []
    // Iterate over all dates to create categories
    let headerHpData = []
    let bpHpData = []
    let bpHpMap = [
      'rr',
      'bpMax',
      'bpMin',
      ...visibleO2SaturationKeys,
    ]
    let bpHpCat = [
      'Respiration Rate',
      'Systolic',
      'Diastolic',
      ...visibleO2SaturationFields.map(({ label }) => label),
    ]
    let whHpData = []
    let whHpMap = ['weight', 'height', 'bmi']
    let suHpData = []
    let suHpMap = ['stools', 'urine']
    let iovHpData = []
    let iovHpMap = ['intakeTotal', 'outputTotal']
    let iotHpData = []
    let iotHpMap = ['intakeTotal', 'outputTotal']
    let scoreHpData = []
    let scoreHpMap = [
      'fallScore',
      'painScale',
      'ppsScore',
      'sedationScore',
      'adl',
      'dtx',
      'others',
    ]

    allDates.forEach((date, dIndex) => {
      const times = result[date] || {} // Get times if the date exists, otherwise default to an empty object

      const categoriesScale = (timeScale === 'hour'
        ? [...timeCategories]
        : dateList[date]) || [...timeCategories]

      categories.push({
        name: date,
        categories: categoriesScale,
      })

      // categories.push({
      //   name: date,
      //   categories: [...timeCategories],
      // })

      let whValue = { weight: null, height: null, bmi: null }
      // Populate vitalData
      // timeCategories.forEach((timeKey, tIndex) => {
      //   Object.entries(vitalData).forEach(([key]) => {
      //     // Push the value if it exists, otherwise push null
      //     const value = times[timeKey] ? times[timeKey][key] : null
      //     vitalData[key].push(value)
      //   })
      //   bpHpMap.map((hpKey, index) => {
      //     const value = times[timeKey] ? times[timeKey][hpKey] : null
      //     bpHpData.push({
      //       x: dIndex * timeCategories.length + tIndex,
      //       y: index,
      //       value,
      //       color: 'white',
      //     })
      //   })

      //   suHpMap.map((hpKey, index) => {
      //     const value = times[timeKey] ? times[timeKey][hpKey] : null
      //     suHpData.push({
      //       x: dIndex * timeCategories.length + tIndex,
      //       y: index,
      //       value,
      //       color: 'white',
      //     })
      //   })

      //   whHpMap.map((whKey, index) => {
      //     const value = times[timeKey] ? times[timeKey][whKey] : null

      //     whValue[whKey] = value || whValue[whKey]
      //   })

      //   scoreHpMap.map((hpKey, index) => {
      //     const value = times[timeKey] ? times[timeKey][hpKey] : null
      //     scoreHpData.push({
      //       x: dIndex * timeCategories.length + tIndex,
      //       y: index,
      //       value,
      //       color: 'white',
      //     })
      //   })
      // })

      categoriesScale.forEach((timeKey, tIndex) => {
        Object.entries(vitalData).forEach(([key]) => {
          // Push the value if it exists, otherwise push null
          const value = times?.[timeKey] ? times[timeKey][key] : ''
          const inPatientVitalSignId = times?.[timeKey]
            ? times[timeKey]?.vitalSignIds?.[key] ||
              times[timeKey]?.inPatientVitalSignId
            : 0
          const commentData = times?.[timeKey]?.commentData || {}
          const commentList = commentData?.[key] || []
          if (value == '') {
            vitalData[key].push('')
          } else {
            vitalData[key].push({
              y: value,
              date: date,
              time: timeKey,
              key: key,
              inPatientVitalSignId,
              commentList,
            })
          }
        })

        bpHpMap.map((hpKey, index) => {
          const value = times[timeKey] ? times[timeKey][hpKey] : null
          bpHpData.push({
            x: dIndex * categoriesScale.length + tIndex,
            y: index,
            value,
            color: 'white',
          })
        })

        //
        suHpMap.map((hpKey, index) => {
          const value = times[timeKey] ? times[timeKey][hpKey] : null

          suHpData.push({
            x: dIndex * categoriesScale.length + tIndex,
            y: index,
            value,
            color: 'white',
          })
        })

        whHpMap.map((whKey, index) => {
          const value = times[timeKey] ? times[timeKey][whKey] : null

          whValue[whKey] = value || whValue[whKey]
        })

        scoreHpMap.map((hpKey, index) => {
          const value = times[timeKey] ? times[timeKey][hpKey] : null
          scoreHpData.push({
            x: dIndex * categoriesScale.length + tIndex,
            y: index,
            value,
            color: 'white',
          })
        })
      })

      let iotValue = { intakeTotal: 0, outputTotal: 0, netBalance: 0 }
      const dateRecords = sortedData.filter(
        (item) => (item.date || item.jsonData?.date) === date,
      )
      const fluidFallbackKeys = {
        intakeTotal: ['intakeOralFluids', 'intakeParenteral'],
        outputTotal: ['outputUrine', 'outputOther'],
      }
      const iovSlotValues = threeSlotCategories.reduce((acc, slot) => {
        acc[slot] = iovHpMap.reduce((slotAcc, hpKey) => {
          slotAcc[hpKey] = { total: 0, hasValue: false }
          return slotAcc
        }, {})
        return acc
      }, {})

      dateRecords.forEach((item) => {
        const slot = mapToThreeSlot(item.time || item.jsonData?.time)

        if (!threeSlotCategories.includes(slot)) return

        iovHpMap.forEach((hpKey) => {
          const value = parseNumberValue(
            getFluidTotal(item.jsonData, hpKey, fluidFallbackKeys[hpKey]),
          )
          if (value === null) return

          iovSlotValues[slot][hpKey].total += value
          iovSlotValues[slot][hpKey].hasValue = true
          iotValue[hpKey] += value
        })
      })

      threeSlotCategories.forEach((timeKey, tIndex) => {
        iovHpMap.map((hpKey, index) => {
          const slotValue = iovSlotValues[timeKey][hpKey]
          const value = slotValue.hasValue ? slotValue.total : null
          iovHpData.push({
            x: dIndex * threeSlotCategories.length + tIndex,
            y: index,
            value,
            color: 'white',
          })
        })
      })

      threeSlotTimes = threeSlotTimes.concat([...threeSlotCategories])
      allTimes = allTimes.concat([...categoriesScale])

      whHpMap.map((whKey, index) => {
        whHpData.push({
          x: dIndex,
          y: index,
          value: whValue[whKey],
          color: 'white',
        })
      })

      iotValue.netBalance = iotValue.intakeTotal - iotValue.outputTotal

      iotHpMap.map((key, index) => {
        iotHpData.push({
          x: dIndex,
          y: index,
          value: iotValue[key],
          color: 'white',
        })
        if (key === 'outputTotal') {
          iotHpData.push({
            x: dIndex,
            y: index + 1,
            value: iotValue['netBalance'],
            color: 'white',
          })
        }
      })

      const durationInDays = moment(date).diff(moment(checkInDate), 'days') * 1
      let durationDisplay = ' - '
      if (durationInDays === 0) {
        durationDisplay = 'Admit'
      } else if (durationInDays < 0) {
        durationDisplay = ' - '
      } else {
        durationDisplay = durationInDays
      }
      headerHpData.push({ x: dIndex, y: 0, value: date, color: 'white' })
      headerHpData.push({
        x: dIndex,
        y: 1,
        value: durationDisplay,
        color: 'white',
      })
      headerHpData.push({ x: dIndex, y: 2, value: '', color: 'white' })
    })

    // If result is empty, generate categories based on startDate and endDate
    // if (Object.entries(result).length === 0) {

    //   // Loop through each day in the range from startDate to endDate
    //   while (start.isSameOrBefore(end)) {
    //     categories.push({
    //       name: start.format('YYYY-MM-DD'), // Format the date as needed
    //       categories: [...timeCategories],
    //     });

    //     // Populate vitalData with null for each time category
    //     timeCategories.forEach((timeKey) => {
    //       Object.entries(vitalData).forEach(([key]) => {
    //         vitalData[key].push(null); // Push null for each key
    //       });
    //     });

    //     // Move to the next day
    //     start.add(1, 'days');
    //   }
    // }

    mercury.xAxis.categories = categories
    mercury.xAxis.labels.enabled = true
    mercury.series[0].data = vitalData['temp']
    mercury.series[1].data = vitalData['hr']
    mercury.series[2].data = vitalData['rr']
    mercury.chart.height = chartHeights.mercury

    monHeader.xAxis.categories = allDates
    monHeader.series[0].data = headerHpData
    monHeader.yAxis.categories = ['Date ', 'Day Admission', 'After Operation']
    applyPrintYAxisLabels(monHeader)
    monHeader.chart.height = chartHeights.header

    let monMercury = _.cloneDeep(mercury)
    monMercury.xAxis.categories = allTimes
    monMercury.tooltip.enabled = false

    monBpHeatmap.xAxis.categories = allTimes
    monBpHeatmap.series[0].data = bpHpData
    monBpHeatmap.yAxis.categories = bpHpCat
    applyPrintYAxisLabels(monBpHeatmap)
    monBpHeatmap.chart.height = getBpChartHeight(bpHpCat.length)

    monWhHeatmap.xAxis.categories = allDates
    monWhHeatmap.series[0].data = whHpData
    monWhHeatmap.yAxis.categories = ['Weight', 'Height', 'BMI']
    applyPrintYAxisLabels(monWhHeatmap)
    monWhHeatmap.chart.height = chartHeights.wh

    monSuHeatmap.xAxis.categories = allTimes
    monSuHeatmap.yAxis.categories = ['Stool', 'Urine']
    monSuHeatmap.series[0].data = suHpData
    applyPrintYAxisLabels(monSuHeatmap)
    monSuHeatmap.chart.height = chartHeights.su

    monIOVHeatmap.xAxis.categories = threeSlotTimes
    monIOVHeatmap.yAxis.categories = ['Intake Vol(cc)', 'Output Vol(cc)']
    monIOVHeatmap.series[0].data = iovHpData
    applyPrintYAxisLabels(monIOVHeatmap)
    monIOVHeatmap.chart.height = chartHeights.iov

    monIOTHeatmap.xAxis.categories = allDates
    monIOTHeatmap.yAxis.categories = [
      'Intake T.IN 24',
      'Output T.IN 24',
      'Net Balance',
    ]
    monIOTHeatmap.series[0].data = iotHpData
    applyPrintYAxisLabels(monIOTHeatmap)
    monIOTHeatmap.chart.height = chartHeights.iot

    monScoreHeatmap.xAxis.categories = allTimes
    monScoreHeatmap.yAxis.categories = [
      'Fall Score',
      'Pain Score',
      'PPS Score',
      'Sedation Score',
      'ADL',
      'DTX',
      'Others',
    ]
    monScoreHeatmap.series[0].data = scoreHpData
    applyPrintYAxisLabels(monScoreHeatmap)
    monScoreHeatmap.chart.height = chartHeights.score

    bpOptions.xAxis.categories = categories
    bpOptions.xAxis.labels.enabled = false
    bpOptions.series[0].data = vitalData['bpMax']
    bpOptions.series[1].data = vitalData['bpMin']

    // Combine bpMin and bpMax into an array of [min, max]
    const bpData = vitalData.bpMin.map((min, index) => [
      min,
      vitalData.bpMax[index],
    ])

    // Assign the combined data to Highcharts options
    bpPrOptions.xAxis.categories = categories
    bpPrOptions.xAxis.labels.enabled = false
    bpPrOptions.series[0].data = bpData
    bpPrOptions.series[1].data = vitalData['hr']

    //painOptions.xAxis.labels.enabled = false
    painOptions.xAxis.opposite = false
    painOptions.xAxis.categories = categories
    painOptions.series[0].data = vitalData['painScale']

    otherOptions.xAxis.categories = categories
    otherOptions.series[0].data = vitalData['others']

    //console.log('cat', mercury)

    setMercury({ ...mercury })
    setMonMercury(monMercury)
    setMonBpHeatmap({ ...monBpHeatmap })
    setMonWhHeatmap({ ...monWhHeatmap })
    setMonIOTHeatmap({ ...monIOTHeatmap })
    setMonIOVHeatmap({ ...monIOVHeatmap })
    setMonScoreHeatmap({ ...monScoreHeatmap })
    setBPOptions({ ...bpOptions })
    setPainOptions({ ...painOptions })
    setOtherOptions({ ...otherOptions })
    setBpPrOptions({ ...bpPrOptions })
    setIsLoading(false)

    return {
      startDate,
      endDate,
      monMercury: { ...mercury },
      monBpHeatmap: { ...monBpHeatmap },
      monWhHeatmap: { ...monWhHeatmap },
      monSuHeatmap: { ...monSuHeatmap },
      monIOVHeatmap: { ...monIOVHeatmap },
      monIOTHeatmap: { ...monIOTHeatmap },
      monScoreHeatmap: { ...monScoreHeatmap },
    }
  }

  useEffect(() => {
    if (tabKey === 'chart') {
      bpOptions.xAxis.labels.enabled = false
      bpPrOptions.xAxis.labels.enabled = false
      //painOptions.xAxis.labels.enabled = false
      painOptions.xAxis.opposite = false
    } else {
      bpOptions.xAxis.labels.enabled = true
      bpPrOptions.xAxis.labels.enabled = true
      //painOptions.xAxis.labels.enabled = true
      painOptions.xAxis.opposite = true
    }
    setBPOptions({ ...bpOptions })
    setBpPrOptions({ ...bpPrOptions })
    setPainOptions({ ...painOptions })
  }, [tabKey])

  const handleOnchangeDateRange = async (e) => {
    setIsLoading(true)
    setValueDateRange(e.value)
    const startDate = moment(e.value[0]).format('YYYY-MM-DD')
    const endDate = moment(e.value[1]).format('YYYY-MM-DD')
    await refreshData(startDate, endDate, true)
    setIsLoading(false)
  }
  const loadPreWeek = async () => {
    setIsLoading(true)

    // Set the startDate to 7 days before the original start date
    const startDate = moment(valueDateRange[0])
      .subtract(6, 'days')
      .format('YYYY-MM-DD')

    // Set the endDate to the original end date (0 can be interpreted as the start of the next day)
    const endDate = moment(valueDateRange[0]).format('YYYY-MM-DD') // Assuming you want to keep the original end date

    setValueDateRange([
      moment(valueDateRange[0]).subtract(6, 'days').toDate(),
      moment(valueDateRange[0]).toDate(),
    ])

    await refreshData(startDate, endDate, true)
    setIsLoading(false)
  }

  const loadNextWeek = async () => {
    setIsLoading(true)

    // Set the startDate to 7 days after the original start date
    const endDate = moment(valueDateRange[1])
      .add(6, 'days')
      .format('YYYY-MM-DD')

    // Keep the endDate as the original end date
    const startDate = moment(valueDateRange[1]).format('YYYY-MM-DD')

    setValueDateRange([
      moment(valueDateRange[1]).toDate(),
      moment(valueDateRange[1]).add(6, 'days').toDate(),
    ])

    await refreshData(startDate, endDate, true)
    setIsLoading(false)
  }

  const renderOptionTemplate = (checkStartEndDate, handleChangeDate) => {
    let startOfDay = DateDefine.startOfToday
    //let find = vnList.find(({ an, vn, hn }) => an === an)
    if (admitInfo) {
      startOfDay = new Date(admitInfo?.checkInDate)
    }
    return (
      <>
        <li
          className={classNames('list-group-item', {
            'text-primary font-weight-bold': checkStartEndDate(
              startOfDay,
              DateDefine.endOfToday,
            ),
          })}
          onClick={() => handleChangeDate([startOfDay, DateDefine.endOfToday])}
        >
          ตั้งแต่ Admit
        </li>
        {firstDateVital && (
          <li
            className={classNames('list-group-item', {
              'text-primary font-weight-bold': checkStartEndDate(
                firstDateVital,
                DateDefine.endOfToday,
              ),
            })}
            onClick={() =>
              handleChangeDate([firstDateVital, DateDefine.endOfToday])
            }
          >
            ตั้งแต่ลง Vital Sign
          </li>
        )}
      </>
    )
  }

  const onSaveCallback = () => {
    console.log('save callback success', valueDateRange[0], valueDateRange[1])
    loadVitalSign(
      moment(valueDateRange[0]).format('YYYY-MM-DD'),
      moment(valueDateRange[1]).format('YYYY-MM-DD'),
    )
  }

  const handlePrintAll = async () => {
    const checkInDate = admitInfo.checkInDate // Assuming admitInfo contains checkInDate
    const startDate = moment(checkInDate, 'YYYY-MM-DD') // Parse checkInDate
    const printList = []

    // Calculate intervals and fetch data
    let currentStart = startDate.clone()
    let index = 0
    while (currentStart.isBefore(moment())) {
      const currentEnd = currentStart.clone().add(6, 'days') // End date is 6 days after start

      // Set the start and end dates for refreshData

      const monItem = await refreshData(
        currentStart.format('YYYY-MM-DD'),
        currentEnd.format('YYYY-MM-DD'),
        true,
      )

      // Push the result to printList
      printList.push({
        printId: 'printSection-' + index,
        ..._.cloneDeep(monItem),
      })

      // Move to the next 7-day interval
      currentStart = currentEnd.clone().add(1, 'day')
    }
    console.log(printList)
    setPrintList(printList)

    await new Promise((resolve) => setTimeout(() => {}, 3000))
    // Do something with printList (e.g., log it or trigger a print)

    // handlePrint()
  }

  const handlePrint = () => {
    const printWindow = window.open('', '_blank')
    const contentToPrint = document.getElementById('printSection').innerHTML
    const patientToPrint = document.getElementById('patient-info-vs').innerHTML

    const pages = printList
      .map(({ printId }) => {
        const content = document.getElementById(printId).innerHTML
        return `
        <div class="print-page2">
          <!-- Patient Info (repeated on each page) -->
          <div id="patient-info-vs">
            ${patientToPrint}
          </div>
          <hr style="border-top: 1px solid #ddd; margin: 10px 0;">
          <!-- Individual Content -->
          <div class="individual-content">
            ${content}
          </div>
        </div>
        <div style="page-break-after: always;"></div> <!-- Ensure each page breaks -->
      `
      })
      .join('') // Join all pages into a single HTML string

    // Set a flag that we are in printing mode
    setIsPrinting(true)

    printWindow.document.write(`
      <html>
        <head>
          <style>
            @page {
                size: A4 landscape; /* Specify landscape orientation */
                margin: 0; /* Remove default margins */
              }          
            /* Make it full screen for printing */
            body {
              margin: 0;
              padding: 0;
              display: flex;
              justify-content: center;
              align-items: center;
              overflow: hidden;
            }

            #printSection {
              width: 100%;
              /*height: 100vh;  Full screen when printing */
            }
            .print-page2 {
                width: 21cm;
                height: 29.7cm;                
                box-sizing: border-box;
                margin: 0 auto;
                overflow: hidden;
                background: #fff;
                padding: 0.5in;              
            }
            .table {
              width: 100%;
              
            }
            .table-bordered,.table-bordered td {
                border: 1px solid #e2e5e8;
            }
            #printArea {
              padding: 0.25in; /* Add padding inside the print area */
              box-sizing: border-box; /* Ensure padding is included in the total width */              
              height: 210mm; /* A4 width */
              width: 297mm; /* A4 height */
              padding: 0.25in; /* Padding around the entire print area */
              box-sizing: border-box; /* Include padding in total dimensions */              
            }

            .content {
              justify-content: space-between; /* Place two contents side by side */
            }

            .content > div {
              border: 1px solid #e2e5e8; /* Optional: for visual separation */
              margin: 0 0.1in; /* Space between the two sections */
            }

            #printSection [style*="overflow: hidden"] {
              overflow: visible !important;
            }

            /* Specific styles for printing */
            @media print {
              body {
                height: 100%;
                display: block;
                overflow: visible;
              }
              #printSection {
                width: 100%;
                /*height: 100vh;  Full screen */
              }
              #printSection [style*="overflow: hidden"] {
                  overflow: visible !important;
              }                
              .print-page2 {
                width: 21cm;
                height: 29.7cm;                
                box-sizing: border-box;
                margin: 0 auto;
                overflow: hidden;
                background: #fff;
                padding: 0.5in;
              }
            }
          </style>
        </head>
        <body>
          <div id='printArea'>
            ${pages}
          </div>
          <script>
            window.print();
            window.onafterprint = function() {
              window.close();
              //window.opener.document.getElementById('printButton').style.display = 'block'; // Show the print button
            };
          </script>
        </body>
      </html>
    `)
    //   <div class='content'>
    //   <div id='patient-info-vs'>${patientToPrint}</div>
    //   <div id="printSection">${contentToPrint}</div>
    // </div>

    //printWindow.document.close();

    // Hide the print button while printing
    //document.getElementById('printButton').style.display = 'none';

    // Listen for when the user has completed printing (or canceled)
    // window.onafterprint = function() {
    //   // Revert the UI back to the normal screen
    //   setIsPrinting(false); // Update the state when print process finishes
    //   //document.getElementById('printButton').style.display = 'block'; // Show the print button again
    //   printWindow.document.close();
    // };
  }

  const renderPatientInfo = () => {
    // /ipd/in-patient-admit/profile?an=990228090257&include=patient.person,patient.Attachment,PatientAllergy,Ward
    return (
      <div id="patient-info-vs">
        <table className="table table-bordered table-patient-info-vs">
          <tbody>
            <tr>
              <td colSpan="2">
                Name of Patient :{' '}
                {admitInfo?.patient?.person?.fullName || ' - '}
              </td>
              <td>Age : {admitInfo?.patient?.person?.ageText || ' - '}</td>
              <td>Ward : {admitInfo?.Ward?.name}</td>
            </tr>
            <tr>
              <td>HN : {admitInfo?.hn}</td>
              <td>AN : {admitInfo?.an}</td>
              <td>Bed : {admitInfo?.bedCode}</td>
              <td>
                Attending Physician : {admitInfo?.primaryMedicCode || ' - '}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    )
  }

  const renderPrintAll = () => {
    return (
      <div>
        {printList.map((item) => {
          return renderMonitoringChart(item)
        })}
      </div>
    )
  }

  const renderMonitoringChart = ({
    printId,
    monMercury,
    monBpHeatmap,
    monWhHeatmap,
    monSuHeatmap,
    monIOVHeatmap,
    monIOTHeatmap,
    monScoreHeatmap,
  }) => {
    return (
      <div id={printId}>
        <HighchartsReact highcharts={Highcharts} options={monHeader} />
        <HighchartsReact highcharts={Highcharts} options={monMercury} />
        <HighchartsReact highcharts={Highcharts} options={monBpHeatmap} />
        <HighchartsReact highcharts={Highcharts} options={monWhHeatmap} />
        <HighchartsReact highcharts={Highcharts} options={monSuHeatmap} />
        <HighchartsReact highcharts={Highcharts} options={monIOVHeatmap} />
        <HighchartsReact highcharts={Highcharts} options={monIOTHeatmap} />
        <HighchartsReact highcharts={Highcharts} options={monScoreHeatmap} />
      </div>
    )
  }

  return (
    <div className={isPrintMode ? 'monitor-chart-print p-0' : 'p-2'}>
      {props.showInput &&
        props.showInput ===
          true(
            <Row>
              <Col md={5} className="d-flex">
                <DateRangePickerCustom
                  value={valueDateRange}
                  onChange={handleOnchangeDateRange}
                  renderOptionTemplate={renderOptionTemplate}
                />
                <div className="d-flex ml-3">
                  <Button variant="outline-primary" onClick={loadPreWeek}>
                    <i className="fa fa-arrow-left" />
                  </Button>
                  <Button variant="outline-primary" onClick={loadNextWeek}>
                    <i className="fa fa-arrow-right" />
                  </Button>
                </div>
              </Col>
            </Row>,
          )}

      <Row>
        <Col md={12}>
          <div onMouseMove={handleMouseMove} onTouchMove={handleMouseMove}>
            {isLoading ? (
              <ChartSkeleton />
            ) : (
              renderMonitoringChart({
                printId: 'printSection',
                monMercury,
                monBpHeatmap,
                monWhHeatmap,
                monSuHeatmap,
                monIOVHeatmap,
                monIOTHeatmap,
                monScoreHeatmap,
              })
            )}
          </div>
        </Col>
        {/* <Col md={12} className="text-center mt-1 mb-1">
          <span>
            {`ระหว่างวันที่  ${valueDateRange[0] ? formatDate(moment(valueDateRange[0])) : '-'
              }  ถึงวันที่  ${valueDateRange[1] ? formatDate(moment(valueDateRange[1])) : '-'
              }`}
          </span>
        </Col> */}
      </Row>
      {renderPatientInfo()}
    </div>
  )
}
